import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { SourceConnection } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head, Measure } from "@/ui/frame/Page";
import { Button } from "@/ui/Button";
import { OrgSettingsScreens } from "@/features/org/screens";
import { Dialog } from "@/ui/Overlay";
import { Field, Input } from "@/ui/Field";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/o/$orgId/connections")({
  component: ConnectionsScreen,
});

type ProviderMeta = {
  name: string;
  idLabel: string;
  idHint: string;
  credentialLabel: string;
  credentialHint: string;
  selfHosted: boolean;
  /** Whether the connection carries a secret of its own. */
  needsCredential: boolean;
  /** Whether the provider can be installed by being sent there and back. */
  installable: boolean;
};

const PROVIDER: Record<string, ProviderMeta> = {
  github: {
    name: "GitHub",
    idLabel: "App installation ID",
    idHint: "From the URL of the Drumlin app's installation in your GitHub account.",
    // GitHub connections hold no secret. The App's private key belongs to
    // Drumlin and is the same for every installation, so an installation id is
    // the whole grant — this form used to ask for that key, which is a secret
    // no customer should hold and which the provider never read.
    credentialLabel: "",
    credentialHint: "",
    selfHosted: false,
    needsCredential: false,
    installable: true,
  },
  gitea: {
    name: "Gitea",
    idLabel: "Account or organisation",
    idHint: "The owner whose repositories you want to deploy.",
    credentialLabel: "Access token",
    credentialHint: "A token with read access to the repositories. Stored encrypted; never shown again.",
    selfHosted: true,
    needsCredential: true,
    installable: false,
  },
};

/**
 * Where code comes from.
 *
 * Connecting a provider is the one moment a customer hands Drumlin a
 * credential, so the screen says plainly what happens to it: verified before it
 * is stored, encrypted at rest, and never returned. A connection that silently
 * failed to authenticate would surface later as builds that do not start, so
 * the API checks the credential before saving it.
 */
function ConnectionsScreen() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [connecting, setConnecting] = useState<string | null>(null);

  const sources = useQuery({
    queryKey: ["source-connections", orgId],
    queryFn: ({ signal }) =>
      api.get<{ connections: SourceConnection[] | null; available: string[] | null }>(
        `/organisations/${orgId}/source-connections`,
        undefined,
        signal,
      ),
  });

  // "Connect" for an installable provider is a round trip, not a form: the
  // person is sent to the provider, chooses repositories there, and comes back
  // to /connections/github. A region that cannot start that — no App
  // configured — answers 501, and the paste-an-id form is still there.
  const beginInstall = useMutation({
    mutationFn: (provider: string) =>
      api.post<{ url: string }>(`/organisations/${orgId}/source-connections/${provider}/install`),
    onSuccess: (result) => {
      window.location.assign(result.url);
    },
    onError: (error, provider) => {
      if (error instanceof ApiError && error.status === 501) {
        setConnecting(provider);
        return;
      }
      toast.error(error);
    },
  });

  // One grant at a time, not one provider at a time.
  //
  // A GitHub App installation covers exactly one account, so an organisation
  // that deploys from both a person's repositories and a GitHub organisation's
  // holds two. Disconnecting "GitHub" would take both away, and this screen
  // used to offer nothing else.
  const disconnect = useMutation({
    mutationFn: (connection: SourceConnection) =>
      api.delete<void>(
        `/organisations/${orgId}/source-connections/${connection.provider}/${connection.id}`,
      ),
    onSuccess: async (_result, connection) => {
      toast.success(`${connection.display_name} disconnected`);
      await queryClient.invalidateQueries({ queryKey: ["source-connections", orgId] });
    },
    onError: toast.error,
  });

  const connections = sources.data?.connections ?? [];
  const available = sources.data?.available ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Measure>
        <Head
          title="Connections"
          description="Where Drumlin gets your code from. A GitHub installation covers one account, so connect your own and each organisation you deploy from."
          screens={<OrgSettingsScreens />}
        />
        <div className="flex flex-col gap-8">
      {connecting && (
        <ConnectDialog
          orgId={orgId}
          provider={connecting}
          onClose={() => setConnecting(null)}
        />
      )}

      {sources.isLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : sources.isError ? (
        <ErrorState error={sources.error} onRetry={() => void sources.refetch()} />
      ) : available.length === 0 ? (
        /* A real empty state rather than a sentence on the page. It read as
           something somebody forgot to finish, which for a screen whose whole
           job is "connect a provider" is the wrong impression to leave. */
        <Panel padded={false}>
          <EmptyState
            title="No providers here yet"
            description="This region has no source providers configured, so there is nothing to connect. A service can still be deployed from a container image."
          />
        </Panel>
      ) : (
        <div className="flex flex-col gap-3">
          {available.map((provider) => {
            const meta = PROVIDER[provider];
            const grants = connections.filter((c) => c.provider === provider);
            const connect = () =>
              meta?.installable ? beginInstall.mutate(provider) : setConnecting(provider);
            return (
              <Panel key={provider}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {meta?.name ?? provider}
                    </p>
                    <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
                      {grants.length === 0
                        ? "Not connected."
                        : grants.length === 1
                          ? "One account connected."
                          : `${grants.length} accounts connected.`}
                    </p>
                  </div>
                  <Button
                    tone={grants.length === 0 ? "secondary" : "ghost"}
                    size="dense"
                    busy={beginInstall.isPending && beginInstall.variables === provider}
                    onClick={connect}
                  >
                    {grants.length === 0 ? "Connect" : "Add another account"}
                  </Button>
                </div>

                {grants.length > 0 && (
                  /* One row per grant, each with its own Disconnect. The
                     account name is the point: "Connected as GitHub" said only
                     which provider it was, which the heading already says. */
                  <ul className="mt-3 flex flex-col divide-y divide-[var(--border)] border-t border-[var(--border)]">
                    {grants.map((grant) => (
                      <li
                        key={grant.id}
                        className="flex flex-wrap items-center justify-between gap-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium text-[var(--text-primary)]">
                            {grant.display_name}
                          </p>
                          <p
                            className="mt-0.5 text-xs text-[var(--text-muted)]"
                            title={absoluteTime(grant.connected_at)}
                          >
                            Connected {relativeTime(grant.connected_at)}
                          </p>
                        </div>
                        <Button
                          tone="ghost"
                          size="dense"
                          busy={disconnect.isPending && disconnect.variables?.id === grant.id}
                          onClick={() => disconnect.mutate(grant)}
                        >
                          Disconnect
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            );
          })}
        </div>
      )}
        </div>
      </Measure>
    </div>
  );
}

function ConnectDialog({
  orgId,
  provider,
  onClose,
}: {
  orgId: string;
  provider: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const meta = PROVIDER[provider];

  const [externalId, setExternalId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [credential, setCredential] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const connect = useMutation({
    mutationFn: () =>
      api.put<{ connection: SourceConnection }>(
        `/organisations/${orgId}/source-connections/${provider}`,
        {
          external_id: externalId.trim(),
          display_name: displayName.trim() || externalId.trim(),
          credential: credential.trim(),
          ...(meta?.selfHosted && baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
        },
      ),
    onSuccess: async () => {
      toast.success(`${meta?.name ?? provider} connected`);
      onClose();
      await queryClient.invalidateQueries({ queryKey: ["source-connections", orgId] });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors();
        if (Object.keys(fields).length > 0) {
          setErrors(fields);
          return;
        }
        // The credential is verified before it is stored, so a rejection here
        // is about the credential itself and belongs next to that field.
        setErrors({ credential: error.message });
        return;
      }
      toast.error(error);
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Connect ${meta?.name ?? provider}`}
      description={
        meta?.needsCredential === false
          ? "The installation is checked against the provider before it is stored."
          : "The credential is checked before it is stored, so you find out now if it is wrong."
      }
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            tone="primary"
            busy={connect.isPending}
            disabled={
              !externalId.trim() || (meta?.needsCredential !== false && !credential.trim())
            }
            onClick={() => connect.mutate()}
          >
            Connect
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={meta?.idLabel ?? "Account"} hint={meta?.idHint} error={errors.external_id} required>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={externalId}
              autoFocus
              onChange={(e) => {
                setExternalId(e.target.value);
                setErrors({});
              }}
            />
          )}
        </Field>

        <Field label="Name" hint="What to call this connection in Drumlin." error={errors.display_name}>
          {({ id }) => (
            <Input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          )}
        </Field>

        {meta?.selfHosted && (
          <Field
            label="Server address"
            hint="For a self-hosted server. Leave empty to use the hosted one."
            error={errors.base_url}
          >
            {({ id }) => (
              <Input
                id={id}
                value={baseUrl}
                placeholder="https://git.example.com"
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            )}
          </Field>
        )}

        {meta?.needsCredential !== false && (
        <Field
          label={meta?.credentialLabel ?? "Credential"}
          hint={meta?.credentialHint}
          error={errors.credential}
          required
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={credential}
              rows={4}
              spellCheck={false}
              className="mono w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--primary)]"
              onChange={(e) => {
                setCredential(e.target.value);
                setErrors({});
              }}
            />
          )}
        </Field>
        )}
      </div>
    </Dialog>
  );
}
